import { ShareLocation } from "@mui/icons-material";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  RebuildRouteDropdownDefaultActionSystemDefaultReset,
  RebuildRouteDropdownDefaultRebuildHeading,
  RebuildRouteDropdownSkipCacheRow,
  RebuildRouteDropdownSubmitButton,
} from "@ryde/controls";
import {
  formatRebuildRouteSaveCacheButtonCopy,
  formatRebuildRouteSaveCacheButtonTooltip,
  IRebuildRouteDefaultActionOptions,
  RebuildRouteTypeEnum,
  resolveSkipCacheDefaultRebuildType,
  type DirectionsServiceEnum,
  type IRideDto,
} from "@ryde/shared";

interface IRebuildRouteDropdownDefaultActionProps {
  rideDto: Partial<IRideDto>;
  isLoading?: boolean;
  lastUsedProvider?: DirectionsServiceEnum | null;
  systemDefaultProvider: DirectionsServiceEnum;
  onRebuild: (
    rebuildType: RebuildRouteTypeEnum,
    options?: IRebuildRouteDefaultActionOptions,
  ) => void;
}

export const RebuildRouteDropdownDefaultAction: FC<
  IRebuildRouteDropdownDefaultActionProps
> = ({
  rideDto,
  isLoading,
  lastUsedProvider,
  systemDefaultProvider,
  onRebuild,
}) => {
  const { t } = useTranslation();
  const [skipCacheMlPath, setSkipCacheMlPath] = useState(false);

  const hasCustomStationRouteId = Boolean(
    rideDto.metadata?.customStationRouteId,
  );

  const rebuildType = useMemo(
    () =>
      resolveSkipCacheDefaultRebuildType({
        skipCachedPaths: skipCacheMlPath,
        preferSkipCacheWithoutUpdate: hasCustomStationRouteId,
      }),
    [hasCustomStationRouteId, skipCacheMlPath],
  );

  const cacheTooltip = useMemo(() => {
    if (!skipCacheMlPath) {
      return t("general.SKIP_CACHED_PATHS_TOOLTIP");
    }

    if (hasCustomStationRouteId) {
      return t("general.REBUILD_ROUTE_SKIP_CACHE_TOOLTIP");
    }

    return t("general.REBUILD_REFRESH_CACHE_TOOLTIP");
  }, [skipCacheMlPath, hasCustomStationRouteId, t]);

  const checkboxLabel = t("general.SKIP_ML_PATH");

  const fullRebuildButtonCopy = useMemo(
    () =>
      formatRebuildRouteSaveCacheButtonCopy({
        lastUsedProvider,
        systemDefaultProvider,
        skipCachedPaths: skipCacheMlPath,
        t,
      }),
    [lastUsedProvider, skipCacheMlPath, systemDefaultProvider, t],
  );

  const saveCacheButtonTooltip = useMemo(
    () =>
      formatRebuildRouteSaveCacheButtonTooltip({
        skipCachedPaths: skipCacheMlPath,
        t,
      }),
    [skipCacheMlPath, t],
  );

  return (
    <>
      <RebuildRouteDropdownDefaultRebuildHeading
        hasProductionId={Boolean(rideDto.productionId)}
      />

      <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--default">
        <RebuildRouteDropdownSkipCacheRow
          checked={skipCacheMlPath}
          label={checkboxLabel}
          tooltip={cacheTooltip}
          onChange={setSkipCacheMlPath}
        />

        <RebuildRouteDropdownSubmitButton
          label={fullRebuildButtonCopy.title}
          hint={fullRebuildButtonCopy.hint}
          tooltip={saveCacheButtonTooltip}
          startIcon={<ShareLocation />}
          isLoading={isLoading}
          isSecondary
          isSkipCacheActive={skipCacheMlPath}
          onClick={() => onRebuild(rebuildType)}
        />

        <RebuildRouteDropdownDefaultActionSystemDefaultReset
          lastUsedProvider={lastUsedProvider}
          systemDefaultProvider={systemDefaultProvider}
          isLoading={isLoading}
          onResetToSystemDefault={() =>
            onRebuild(RebuildRouteTypeEnum.Default, {
              provider: systemDefaultProvider,
            })
          }
        />
      </div>
    </>
  );
};
